<script setup lang="ts">
import {computed} from "vue"
import {
  Mic,
  AudioLines,
  Activity,
  Headphones,
  MicOff,
  Volume2,
  VolumeX,
  Video,
  VideoOff,
  MonitorUp,
  PhoneOff,
  ChevronDown,
  SwitchCamera, Settings, Sun, Moon, Speaker,
} from "lucide-vue-next"
import {usePlatform} from "~/composables/usePlatform"
import {useSpeakerphone} from "~/composables/useSpeakerphone"
import {isTauriRuntime} from "~/lib/platform/runtime"
import {Button} from "@/components/ui/button"
import {Slider} from "@/components/ui/slider"
import {Label} from "@/components/ui/label"
import {Switch} from "@/components/ui/switch"
import {Checkbox} from "@/components/ui/checkbox"
import {ToggleGroup, ToggleGroupItem} from "@/components/ui/toggle-group"
import {
  DropdownMenu,
  DropdownMenuContent, DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuRadioGroup, DropdownMenuRadioItem,
  DropdownMenuSeparator, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {Tooltip, TooltipContent, TooltipProvider, TooltipTrigger} from "@/components/ui/tooltip"
import MicLevelMeter from "./MicLevelMeter.vue"
import {selectableDevices} from "~/lib/livekit/devicePreferences"
import {MAX_MIC_SENSITIVITY_THRESHOLD, MIN_MIC_SENSITIVITY_THRESHOLD} from "~/lib/settings/appSettings"
import VoiceAvatar from "~/components/voice-ui/VoiceAvatar/VoiceAvatar.vue";

const props = defineProps<{
  isMuted: boolean
  isDeafened: boolean
  isVideoOn: boolean
  isScreenSharing: boolean
  microphones: MediaDeviceInfo[]
  speakers: MediaDeviceInfo[]
  cameras: MediaDeviceInfo[]
  selectedMic: string
  selectedSpeaker: string
  selectedCamera: string
  isNoiseSuppressionEnabled: boolean
  isNoiseSuppressionPending: boolean
  isEchoCancellationEnabled: boolean
  isSelfMonitoring: boolean
  micVolume: number
  speakerVolume: number
  micSensitivity: number
  videoQuality: string
  screenShareQuality: string
  screenShareFps: number
  isScreenShareAudioEnabled: boolean
  micBlocked: boolean
  cameraBlocked: boolean
  screenShareBlocked: boolean
}>()

const emit = defineEmits<{
  (e: 'toggleMute'): void
  (e: 'toggleDeaf'): void
  (e: 'toggleVideo'): void
  (e: 'toggleScreenShare'): void
  (e: 'disconnect'): void
  (e: 'openCameraModal'): void
  (e: 'changeMic', deviceId: string): void
  (e: 'changeSpeaker', deviceId: string): void
  (e: 'changeCamera', deviceId: string): void
  (e: 'toggleNoiseSuppression'): void
  (e: 'toggleEchoCancellation'): void
  (e: 'setSelfMonitoring', enabled: boolean): void
  (e: 'changeMicVolume', volume: number): void
  (e: 'changeSpeakerVolume', volume: number): void
  (e: 'changeMicSensitivity', threshold: number): void
  (e: 'changeVideoQuality', quality: any): void
  (e: 'changeScreenShareQuality', quality: any): void
  (e: 'changeScreenShareFps', fps: number): void
  (e: 'changeScreenSharePreset', preset: 'text' | 'motion'): void
  (e: 'changeScreenShareAudio', enabled: boolean): void
}>()

// The slider reads as "sensitivity" (higher = picks up quieter speech), which is the inverse of
// the underlying analyser-level threshold (higher = requires louder speech) — flip the mapping
// both ways so 100% on the slider always means MIN_MIC_SENSITIVITY_THRESHOLD, not the max.
const sensitivityRange = MAX_MIC_SENSITIVITY_THRESHOLD - MIN_MIC_SENSITIVITY_THRESHOLD
const micSensitivityPercent = computed(() =>
    Math.round(100 * (MAX_MIC_SENSITIVITY_THRESHOLD - props.micSensitivity) / sensitivityRange)
)

function sensitivityPercentToThreshold(percent: number) {
  return MAX_MIC_SENSITIVITY_THRESHOLD - (percent / 100) * sensitivityRange
}

// On mobile there's no device-picker dropdown for the camera (no "choose from a list" UX on a
// phone that has exactly a front and a back camera) — "Flip" just cycles to the next enumerated
// camera, reusing the same `changeCamera` device-id switch the desktop dropdown already emits.
const {isMobile} = usePlatform()
// «Громкая связь»: only in the phone app, where the call can go to the earpiece (useSpeakerphone.ts).
const showSpeakerphone = computed(() => isMobile.value && isTauriRuntime())
const speakerphone = useSpeakerphone()
const nextCameraId = computed(() => {
  if (props.cameras.length < 2) return null
  const currentIndex = props.cameras.findIndex((c) => c.deviceId === props.selectedCamera)
  const nextIndex = (currentIndex + 1) % props.cameras.length
  return props.cameras[nextIndex]?.deviceId ?? null
})

// Phone: every button is a fixed 56px square/circle. With the desktop's text-button padding the
// pill came out ~420px wide — wider than a phone screen — so it spilled off both edges.
// `has-[>svg]:px-0` also cancels the `xl` size's own `has-[>svg]:px-6`.
const mobileButtonClass = 'w-14 px-0 has-[>svg]:px-0'

function handleFlipCamera() {
  if (nextCameraId.value) emit('changeCamera', nextCameraId.value)
}

// Same fallback labeling as the device list items below ("Как в системе" for the default/empty
// selection, the raw device label otherwise) — shown under the sub-menu trigger so the current
// choice is visible without opening it.
const selectedMicLabel = computed(() => {
  if (!props.selectedMic || props.selectedMic === 'default') return 'Как в системе'
  const mic = props.microphones.find((m) => m.deviceId === props.selectedMic)
  return mic ? (mic.label || 'Microphone ' + mic.deviceId) : 'Как в системе'
})
const selectedSpeakerLabel = computed(() => {
  if (!props.selectedSpeaker || props.selectedSpeaker === 'default') return 'Как в системе'
  const speaker = props.speakers.find((s) => s.deviceId === props.selectedSpeaker)
  return speaker ? (speaker.label || 'Speaker ' + speaker.deviceId) : 'Как в системе'
})

// "Прослушать себя" must not outlive the menu it lives in — closing this dropdown (clicking
// outside, Escape) turns it back off, same as toggling the switch directly.
function onMicMenuOpenChange(open: boolean) {
  if (!open && props.isSelfMonitoring) emit('setSelfMonitoring', false)
}

// While listening to yourself, picking a mic/output keeps the menu (and so the monitoring) open —
// comparing devices by ear is exactly what it's for.
function keepOpenWhileMonitoring(event: Event) {
  if (props.isSelfMonitoring) event.preventDefault()
}
</script>

<template>
  <!-- Mobile: a small shadow instead of `shadow-2xl`. The pill sits 16px above the call view's bottom
       edge, which clips (the view is `overflow-hidden`); the big shadow reaches ~50px down, so it was
       cut off in a hard horizontal band under the pill. -->
  <Card
      class="flex gap-2 flex-row absolute bottom-4 left-1/2 -translate-x-1/2 z-10 p-2 rounded-full border-white/10 bg-background/80 backdrop-blur-xl"
      :class="isMobile ? 'shadow-md' : 'shadow-2xl'">
    <TooltipProvider>
      <!-- Microphone -->
      <div class="flex items-center">
        <Tooltip>
          <TooltipTrigger as-child>
            <Button
                :variant="isMuted ? 'destructive' : 'secondary'"
                :size="isMobile ? 'xl' : 'lg'"
                class="rounded-full rounded-r-none"
                :class="isMobile ? mobileButtonClass : 'px-5'"
                :disabled="micBlocked"
                @click="emit('toggleMute')"
            >
              <Mic v-if="!isMuted" class="size-5"/>
              <MicOff v-else class="size-5"/>
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top">{{
              micBlocked ? 'Нет прав на использование микрофона' : isMuted ? 'Включить микрофон' : 'Выключить микрофон'
            }}
          </TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger as-child>
            <Button :variant="isDeafened ? 'destructive' : 'secondary'"
                    :size="isMobile ? 'xl' : 'lg'"
                    :class="isMobile ? [showSpeakerphone ? 'rounded-none' : 'rounded-full rounded-l-none', mobileButtonClass] : 'rounded-none'"
                    :disabled="micBlocked"
                    @click.stop="emit('toggleDeaf')">
              <Volume2 v-if="!isDeafened" class="size-5"/>
              <VolumeX v-else class="size-5"/>
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {{ micBlocked ? 'Нет прав на использование микрофона' : isDeafened ? 'Включить звук' : 'Выключить звук' }}
          </TooltipContent>
        </Tooltip>

        <!-- Phone: the loudspeaker instead of the earpiece, the third segment of the audio group. -->
        <Tooltip v-if="showSpeakerphone">
          <TooltipTrigger as-child>
            <Button
                :variant="speakerphone.enabled.value ? 'default' : 'secondary'"
                size="xl"
                class="rounded-full rounded-l-none"
                :class="mobileButtonClass"
                :aria-pressed="speakerphone.enabled.value"
                aria-label="Громкая связь"
                data-testid="call-speakerphone"
                @click="speakerphone.toggle()"
            >
              <Speaker class="size-5"/>
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top">{{ speakerphone.enabled.value ? 'Выключить громкую связь' : 'Громкая связь' }}</TooltipContent>
        </Tooltip>

        <DropdownMenu v-if="!isMobile" @update:open="onMicMenuOpenChange">
          <DropdownMenuTrigger as-child>
            <Button variant="secondary" size="lg" class="rounded-full rounded-l-none">
              <ChevronDown class="size-4"/>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="center" class="w-auto p-2">
            <div class="flex flex-col w-64">
              <div class="px-2 py-2">
                <div class="flex items-center justify-between mb-2">
                  <Label class="text-[10px] uppercase text-muted-foreground font-bold">Индикатор микрофона</Label>
                </div>
                <MicLevelMeter/>
              </div>
              <DropdownMenuSeparator/>
              <DropdownMenuGroup>
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>

                    <div class="flex gap-4 items-center">
                      <AudioLines class="size-5"/>
                      <div class="grid flex-1 text-left text-sm leading-tight">
                        <span class="truncate font-medium">Громкость</span>
                        <span class="truncate text-xs text-muted-foreground">Чувствительность</span>
                      </div>
                    </div>
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent class="w-54">
                    <DropdownMenuRadioGroup>
                      <div class="px-2 py-2">
                        <div class="flex items-center justify-between mb-2">
                          <Label class="text-[10px] uppercase text-muted-foreground font-bold">Чувствительность
                            микрофона</Label>
                        </div>
                        <Slider
                            :model-value="[micSensitivityPercent]"
                            :min="0"
                            :max="100"
                            :step="1"
                            @update:model-value="(val) => val?.[0] !== undefined && emit('changeMicSensitivity', sensitivityPercentToThreshold(val[0]))"
                        />
                      </div>
                      <DropdownMenuSeparator/>
                      <div class="px-2 py-2">
                        <div class="flex items-center justify-between mb-2">
                          <Label class="text-[10px] uppercase text-muted-foreground font-bold">Громкость
                            микрофона</Label>
                        </div>
                        <Slider
                            :model-value="[Math.pow(micVolume, 1/3) * 100]"
                            :max="100"
                            :step="1"
                            @update:model-value="(val) => emit('changeMicVolume', Math.pow(val[0] / 100, 3))"
                        />
                      </div>
                      <DropdownMenuSeparator/>
                      <div class="px-2 py-2">
                        <div class="flex items-center justify-between mb-2">
                          <Label class="text-[10px] uppercase text-muted-foreground font-bold">Громкость
                            динамика</Label>
                        </div>
                        <Slider
                            :model-value="[Math.pow(speakerVolume, 1/3) * 100]"
                            :max="100"
                            :step="1"
                            @update:model-value="(val) => emit('changeSpeakerVolume', Math.pow(val[0] / 100, 3))"
                        />
                      </div>
                    </DropdownMenuRadioGroup>
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
              </DropdownMenuGroup>
              <DropdownMenuSeparator/>

              <DropdownMenuGroup>
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>
                    <div class="flex gap-4 items-center">
                      <Mic class="size-5"/>
                      <div class="grid flex-1 text-left text-sm leading-tight">
                        <span class="truncate font-medium">Устройство ввода</span>
                        <span class="truncate text-xs text-muted-foreground">{{ selectedMicLabel }}</span>
                      </div>
                    </div>
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent class="w-54">
                    <DropdownMenuRadioGroup>
                      <DropdownMenuItem @select="keepOpenWhileMonitoring" @click="emit('changeMic', 'default')">
                        <div class="flex items-center justify-between w-full">
                          <span class="truncate pr-2">Как в системе</span>
                          <div v-if="selectedMic === 'default' || !selectedMic"
                               class="size-2 rounded-full bg-primary"/>
                        </div>
                      </DropdownMenuItem>
                      <DropdownMenuItem v-for="mic in selectableDevices(microphones)"
                                        :key="mic.deviceId"
                                        @select="keepOpenWhileMonitoring" @click="emit('changeMic', mic.deviceId)">
                        <div class="flex items-center justify-between w-full">
                          <span class="truncate pr-2">{{ mic.label || 'Microphone ' + mic.deviceId }}</span>
                          <div v-if="selectedMic === mic.deviceId" class="size-2 rounded-full bg-primary"/>
                        </div>
                      </DropdownMenuItem>
                    </DropdownMenuRadioGroup>
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
              </DropdownMenuGroup>
              <DropdownMenuGroup>
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>
                    <div class="flex gap-4 items-center">
                      <Headphones class="size-5"/>
                      <div class="grid flex-1 text-left text-sm leading-tight">
                        <span class="truncate font-medium">Устройство вывода</span>
                        <span class="truncate text-xs text-muted-foreground">{{ selectedSpeakerLabel }}</span>
                      </div>
                    </div>
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent class="w-54">
                    <DropdownMenuRadioGroup>
                      <DropdownMenuItem @select="keepOpenWhileMonitoring" @click="emit('changeSpeaker', 'default')">
                        <div class="flex items-center justify-between w-full">
                          <span class="truncate pr-2">Как в системе</span>
                          <div v-if="selectedSpeaker === 'default' || !selectedSpeaker"
                               class="size-2 rounded-full bg-primary"/>
                        </div>
                      </DropdownMenuItem>
                      <DropdownMenuItem v-for="speaker in selectableDevices(speakers)"
                                        :key="speaker.deviceId" @select="keepOpenWhileMonitoring" @click="emit('changeSpeaker', speaker.deviceId)">
                        <div class="flex items-center justify-between w-full">
                          <span class="truncate pr-2">{{ speaker.label || 'Speaker ' + speaker.deviceId }}</span>
                          <div v-if="selectedSpeaker === speaker.deviceId" class="size-2 rounded-full bg-primary"/>
                        </div>
                      </DropdownMenuItem>
                    </DropdownMenuRadioGroup>
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
              </DropdownMenuGroup>
              <DropdownMenuSeparator/>
              <DropdownMenuGroup>
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>
                    <div class="flex gap-4 items-center">
                      <AudioLines class="size-5"/>
                      <div class="grid flex-1 text-left text-sm leading-tight">
                        <div class="grid flex-1 text-left text-sm leading-tight">
                          <span class="truncate font-medium">Шумоподавление</span>
                        </div>
                      </div>
                    </div>
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent class="w-54">
                    <DropdownMenuRadioGroup>
                      <DropdownMenuItem
                          :disabled="isNoiseSuppressionPending"
                          @select.prevent="emit('toggleNoiseSuppression')"
                      >
                        <div class="flex items-center justify-between w-full">
                          <span>Шумоподавление</span>
                          <Switch
                              :model-value="isNoiseSuppressionEnabled"
                              :disabled="isNoiseSuppressionPending"
                              class="pointer-events-none"
                              aria-label="Шумоподавление"
                          />
                        </div>
                      </DropdownMenuItem>

                      <DropdownMenuItem
                          @select.prevent="emit('toggleEchoCancellation')"
                      >
                        <div class="flex items-center justify-between w-full">
                          <span>Эхоподавление</span>
                          <Switch
                              :model-value="isEchoCancellationEnabled"
                              class="pointer-events-none"
                              aria-label="Эхоподавление"
                          />
                        </div>
                      </DropdownMenuItem>
                    </DropdownMenuRadioGroup>
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
              </DropdownMenuGroup>


            </div>

            <DropdownMenuSeparator/>
            <DropdownMenuItem
                @select.prevent="emit('setSelfMonitoring', !isSelfMonitoring)"
            >
              <div class="flex items-center justify-between w-full">
                <span>Прослушать себя</span>
                <Checkbox
                    :model-value="isSelfMonitoring"
                    class="pointer-events-none"
                    aria-label="Прослушать себя"
                />
              </div>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <!-- Video -->
      <div class="flex items-center">
        <div class="relative">
          <Tooltip>
            <TooltipTrigger as-child>
              <Button
                  :variant="isVideoOn ? 'primary' : 'secondary'"
                  :size="isMobile ? 'xl' : 'lg'"
                  :class="[
                    { 'bg-primary text-primary-foreground': isVideoOn },
                    isMobile ? ['rounded-full', mobileButtonClass] : 'rounded-l-full px-5'
                  ]"
                  :disabled="cameraBlocked"
                  @click="emit('toggleVideo')"
              >
                <Video v-if="isVideoOn" class="size-5"/>
                <VideoOff v-else class="size-5"/>

              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">{{
                cameraBlocked ? 'Нет прав на использование камеры' : isVideoOn ? 'Выключить камеру' : 'Включить камеру'
              }}
            </TooltipContent>
          </Tooltip>

          <!-- Mobile: no device-picker dropdown — just a "Flip" button that cycles front/back
  camera, and only while the camera is actually on. -->
          <Tooltip v-if="isMobile && isVideoOn">
            <TooltipTrigger as-child>
              <Button
                  variant="secondary"
                  :size="isMobile ? 'xl' : 'lg'"
                  class="rounded-full absolute left-0 flip-camera-button"
                  :class="mobileButtonClass"
                  :disabled="!nextCameraId"
                  @click="handleFlipCamera"
              >
                <SwitchCamera class="size-5"/>
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">Сменить камеру</TooltipContent>
          </Tooltip>
        </div>

        <DropdownMenu v-if="!isMobile">
          <DropdownMenuTrigger as-child>
            <Button variant="secondary" size="lg" class="rounded-full rounded-l-none">
              <ChevronDown class="size-4"/>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="center" class="w-64">
            <DropdownMenuLabel>Камера</DropdownMenuLabel>
            <DropdownMenuSeparator/>
            <DropdownMenuItem @click="emit('openCameraModal')">
              <Video class="mr-2 size-4"/>
              <span>Выберите камеру…</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator v-if="cameras.length > 0"/>
            <DropdownMenuItem v-for="camera in cameras" :key="camera.deviceId"
                              @click="emit('changeCamera', camera.deviceId)">
              <div class="flex items-center justify-between w-full">
                <span class="truncate pr-2">{{ camera.label || 'Camera ' + camera.deviceId }}</span>
                <div v-if="selectedCamera === camera.deviceId" class="size-2 rounded-full bg-primary"/>
              </div>
            </DropdownMenuItem>

            <DropdownMenuSeparator/>
            <DropdownMenuLabel>Качество видео</DropdownMenuLabel>
            <DropdownMenuItem v-for="q in ['360p', '480p', '720p', '1080p']" :key="q"
                              @click="emit('changeVideoQuality', q)">
              <div class="flex items-center justify-between w-full">
                <span>{{ q }}</span>
                <div v-if="videoQuality === q" class="size-2 rounded-full bg-primary"/>
              </div>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <!-- Screen Share -->
      <div class="flex items-center">
        <Tooltip>
          <TooltipTrigger as-child>
            <Button
                :variant="isScreenSharing ? 'primary' : 'secondary'"
                :size="isMobile ? 'xl' : 'lg'"
                :class="[
                { 'bg-green-600 hover:bg-green-700 text-white': isScreenSharing },
                isMobile ? ['rounded-full', mobileButtonClass] : 'rounded-full rounded-r-none px-5',
              ]"
                :disabled="screenShareBlocked"
                @click="emit('toggleScreenShare')"
            >
              <MonitorUp class="size-5"/>
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top">{{
              screenShareBlocked ? 'Нет прав на демонстрацию экрана' : isScreenSharing ? 'Остановить показ' : 'Демонстрация экрана'
            }}
          </TooltipContent>
        </Tooltip>

        <DropdownMenu v-if="!isMobile">
          <DropdownMenuTrigger as-child>
            <Button variant="secondary" size="lg" class="rounded-full rounded-l-none"
                    :class="{ 'bg-green-600/80 hover:bg-green-700/80 text-white border-green-500': isScreenSharing }">
              <ChevronDown class="size-4"/>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="center" class="w-80 p-2">
            <DropdownMenuLabel>Пресеты</DropdownMenuLabel>
            <div class="grid grid-cols-2 gap-1 px-2 pb-2">
              <Button
                  type="button"
                  size="sm"
                  :variant="screenShareQuality === '1440p' && screenShareFps === 15 ? 'default' : 'outline'"
                  class="h-auto min-h-8 whitespace-normal px-2 py-1.5 text-xs"
                  @click.prevent.stop="emit('changeScreenSharePreset', 'text')"
              >Повышенная чёткость текста
              </Button>
              <Button
                  type="button"
                  size="sm"
                  :variant="screenShareQuality === '720p' && screenShareFps === 30 ? 'default' : 'outline'"
                  class="h-auto min-h-8 whitespace-normal px-2 py-1.5 text-xs"
                  @click.prevent.stop="emit('changeScreenSharePreset', 'motion')"
              >Плавное видео
              </Button>
            </div>
            <DropdownMenuSeparator/>
            <DropdownMenuLabel>Качество</DropdownMenuLabel>
            <ToggleGroup
                :model-value="screenShareQuality"
                type="single"
                variant="outline"
                size="sm"
                class="grid grid-cols-3 px-2 pb-2 w-full"
                @update:model-value="value => value && emit('changeScreenShareQuality', value)"
            >
              <ToggleGroupItem value="720p">720p</ToggleGroupItem>
              <ToggleGroupItem value="1080p">1080p</ToggleGroupItem>
              <ToggleGroupItem value="1440p">1440p</ToggleGroupItem>
            </ToggleGroup>
            <DropdownMenuSeparator/>
            <DropdownMenuLabel>FPS</DropdownMenuLabel>
            <ToggleGroup
                :model-value="String(screenShareFps)"
                type="single"
                variant="outline"
                size="sm"
                class="grid grid-cols-3 px-2 pb-2 w-full"
                @update:model-value="value => value && emit('changeScreenShareFps', Number(value))"
            >
              <ToggleGroupItem
                  v-for="fps in [5, 15, 30]"
                  :key="fps"
                  :value="String(fps)"
                  :disabled="screenShareQuality === '1440p' && fps === 30"
              >{{ fps }}
              </ToggleGroupItem>
            </ToggleGroup>
            <DropdownMenuSeparator/>
            <DropdownMenuItem
                :disabled="!isScreenSharing"
                @select.prevent="emit('changeScreenShareAudio', !isScreenShareAudioEnabled)"
            >
              <div class="flex items-center justify-between gap-3 w-full">
                <span>Передавать звук с приложения</span>
                <Switch
                    :model-value="isScreenShareAudioEnabled"
                    :disabled="!isScreenSharing"
                    class="pointer-events-none"
                    aria-label="Передавать звук с приложения"
                />
              </div>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <!-- Disconnect -->
      <Tooltip>
        <TooltipTrigger as-child>
          <Button
              variant="destructive"
              :size="isMobile ? 'xl' : 'lg'"
              class="rounded-full shadow-lg hover:shadow-destructive/20 transition-all hover:scale-105"
              :class="isMobile ? mobileButtonClass : ''"
              @click="emit('disconnect')"
          >
            <PhoneOff class="size-5"/>
          </Button>
        </TooltipTrigger>
        <TooltipContent side="top">Отключиться</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  </Card>
</template>

<style scoped>
/* The flip-camera button only exists in the DOM while the camera is on (`v-if`), so a plain
   CSS `transition` on `top` never animates — there's no prior state to transition from, it
   just appears already in place. An `animation` runs automatically on insertion instead. */
@keyframes flip-camera-slide-up {
  from {
    top: 0;
  }

  to {
    top: -4.5rem;
  }
}

.flip-camera-button {
  animation: flip-camera-slide-up 250ms cubic-bezier(0.2, 0.9, 0.22, 1) forwards;
}
</style>
